import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DiscussionError, resolveReference, type Reference } from '@peruselab/api-client/discussions';
import { Icon } from '../../components/Icon';
import { ReferenceCitation } from './ReferenceCitation';

/** D19: up to ten numbered references per comment. */
export const MAX_REFERENCES = 10;

interface ReferencePickerProps {
  readonly references: readonly Reference[];
  readonly onChange: (references: readonly Reference[]) => void;
  readonly disabled?: boolean;
}

/**
 * ADR 0016 D19 (#910): the composer's references -- paste an identifier (DOI, PubMed ID, arXiv ID,
 * PubChem CID, ChEBI ID, CAS RN, patent, ISBN, a PeruseLab link or any web address), see it
 * resolved and labelled before posting, remove it if it's wrong.
 */
export function ReferencePicker({ references, onChange, disabled }: ReferencePickerProps) {
  const { t } = useTranslation();
  const inputId = useId();
  const [open, setOpen] = useState(references.length > 0);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<DiscussionError['kind'] | 'duplicate' | 'tooMany' | null>(null);
  const full = references.length >= MAX_REFERENCES;

  const add = async () => {
    const text = input.trim();
    if (!text || busy || full) return;
    setBusy(true);
    setError(null);
    try {
      const reference = await resolveReference(text);
      if (references.some((existing) => existing.id === reference.id)) {
        setError('duplicate');
      } else {
        onChange([...references, reference]);
        setInput('');
      }
    } catch (failure) {
      setError(failure instanceof DiscussionError ? failure.kind : 'unknown');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="reference-picker">
      {references.length > 0 ? (
        <ol className="reference-list reference-list--editing" aria-label={t('discussions.references.listLabel')}>
          {references.map((reference, index) => (
            <li key={reference.id}>
              <ReferenceCitation reference={reference} number={index + 1} />
              <button
                type="button"
                className="icon-button"
                aria-label={t('discussions.references.remove', { number: index + 1 })}
                title={t('discussions.references.remove', { number: index + 1 })}
                disabled={disabled}
                onClick={() => onChange(references.filter((existing) => existing.id !== reference.id))}
              >
                <Icon name="close" size="1rem" />
              </button>
            </li>
          ))}
        </ol>
      ) : null}
      {open ? (
        <div className="reference-picker-add">
          <label htmlFor={inputId} className="visually-hidden">
            {t('discussions.references.inputLabel')}
          </label>
          <input
            id={inputId}
            type="text"
            dir="ltr"
            value={input}
            disabled={disabled || busy || full}
            placeholder={t('discussions.references.placeholder')}
            onChange={(event) => setInput(event.target.value)}
            onKeyDown={(event) => {
              // Inside the comment's form: Enter adds the reference, it doesn't post the comment.
              if (event.key === 'Enter') {
                event.preventDefault();
                void add();
              }
            }}
          />
          <button
            type="button"
            className="icon-button"
            onClick={() => void add()}
            disabled={disabled || busy || full || !input.trim()}
            aria-label={busy ? t('discussions.references.lookingUp') : t('discussions.references.add')}
            title={busy ? t('discussions.references.lookingUp') : t('discussions.references.add')}
          >
            <Icon name="check" />
          </button>
        </div>
      ) : (
        <button
          type="button"
          className="icon-button"
          disabled={disabled || full}
          onClick={() => setOpen(true)}
          aria-label={t('discussions.references.addReference')}
          title={t('discussions.references.addReference')}
        >
          <Icon name="link" />
        </button>
      )}
      {full ? <p className="discussion-note">{t('discussions.references.full', { count: MAX_REFERENCES })}</p> : null}
      {error ? (
        <p className="import-error" role="alert">
          {t(`discussions.references.errors.${error}`)}
        </p>
      ) : null}
    </div>
  );
}
